import type { Log } from 'pages/Logs/hooks';

export type ActivityLogsPageCachePayload = {
  logs: Log[],
  hasMore: boolean,
};

/** In-memory page cache — shared by /logs paginator and /metrics download arrow. */
const pageCache = new Map<number, ActivityLogsPageCachePayload>();

export const rememberActivityLogsPage = (
  pageNum: number,
  payload: ActivityLogsPageCachePayload,
) => {
  if (!payload.logs.length) return;
  pageCache.set(pageNum, payload);
};

export const readActivityLogsPageFromMemory = (pageNum: number) => (
  pageCache.get(pageNum) || null
);

export const clearActivityLogsPageMemoryCache = () => {
  pageCache.clear();
};

/** Persist page to in-memory cache (session only — refresh always hits the network). */
export const persistSharedActivityLogsPage = (
  _organizationId: string,
  pageNum: number,
  payload: ActivityLogsPageCachePayload,
) => {
  rememberActivityLogsPage(pageNum, payload);
};

/** Load one page from memory. */
export const loadSharedActivityLogsPage = (
  _organizationId: string,
  pageNum: number,
): ActivityLogsPageCachePayload | null => (
  readActivityLogsPageFromMemory(pageNum)
);

/** Merge pages 1…loadedPageCount — newest log first, deduped by id. */
export const mergeActivityLogsPages = async (
  _organizationId: string,
  loadedPageCount: number,
  currentPage: number,
  currentPageLogs: Log[] | null | undefined,
): Promise<Log[]> => {
  const pages = Math.max(1, loadedPageCount);
  const byId = new Map<string, Log>();

  for (let pageNum = 1; pageNum <= pages; pageNum += 1) {
    let rows: Log[] = [];
    if (pageNum === currentPage && currentPageLogs?.length) {
      rows = currentPageLogs;
    } else {
      const cached = loadSharedActivityLogsPage('', pageNum);
      rows = cached?.logs || [];
    }
    rows.forEach((log) => {
      const id = String(log.id || '').trim();
      if (!id) return;
      byId.set(id, log);
    });
  }

  return [...byId.values()].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
};
